import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { PlusIcon, XMarkIcon, PhotoIcon } from '@heroicons/react/24/solid';
import { imageService } from '../services/ImageService';

const CarouselItem = ({ uid, imageUID, onClick, onDelete }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchUrl = async () => {
      try {
        const u = await imageService.getImageURL(uid, imageUID, true);
        if (isMounted) {
          setUrl(u);
          setLoading(false);
        }
      } catch (e) {
        if (isMounted) setLoading(false);
      }
    };
    fetchUrl();
    return () => { isMounted = false; };
  }, [uid, imageUID]);

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (window.confirm('Delete this image?')) {
      setDeleting(true);
      await onDelete(imageUID);
      // Don't need to setDeleting(false) because this component will likely unmount
    }
  };

  if (loading) {
    return (
      <div className="relative flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg animate-pulse border border-gray-200"></div>
    );
  }

  if (!url) {
     // Error state
     return (
        <div className="relative flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg border border-red-200 flex items-center justify-center">
            <PhotoIcon className="w-6 h-6 text-gray-400" />
        </div>
     );
  }

  return (
    <div 
      className={`relative flex-shrink-0 w-16 h-16 group cursor-pointer transition-opacity ${deleting ? 'opacity-50' : 'opacity-100'}`}
      onClick={onClick}
    >
      <img
        src={url}
        alt="Note attachment"
        className="w-full h-full object-cover rounded-lg border border-gray-200 shadow-sm"
      />
      
      {/* Delete button - visible on hover or always on mobile? Spec says top-right */}
      <button
        onClick={handleDelete}
        className="absolute -top-2 -right-2 bg-white text-red-500 rounded-full p-0.5 shadow-md hover:bg-red-50 transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100 z-10"
        aria-label="Delete image"
      >
        <XMarkIcon className="w-4 h-4" />
      </button>
    </div>
  );
};

const ImageCarousel = ({ noteId, uid, images, onImagesUpdate, onImageClick }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      // Upload images
      const newImageUIDs = await imageService.uploadImages(files, noteId, uid);
      
      // Update parent state
      // The firestore update is handled in uploadImages, but we need to update local state
      // The `images` prop passed in is the OLD list. We need to append.
      const updatedList = [...(images || []), ...newImageUIDs];
      onImagesUpdate(updatedList);

    } catch (error) {
      console.error("Upload failed:", error);
      alert("Failed to upload images. Please try again.");
    } finally {
      setUploading(false);
      // Reset input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteImage = async (imageUID) => {
    try {
      await imageService.deleteImage(noteId, uid, imageUID);
      const updatedList = (images || []).filter(id => id !== imageUID);
      onImagesUpdate(updatedList);
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete image.");
    }
  };

  return (
    <div className="flex items-center gap-3 overflow-x-auto py-2 px-1 scrollbar-hide -mx-1 px-1">
      {/* Image List */}
      {images && images.map((imageUID, index) => (
        <CarouselItem
          key={imageUID}
          uid={uid}
          imageUID={imageUID}
          onClick={() => onImageClick(index)}
          onDelete={handleDeleteImage}
        />
      ))}

      {/* Add Image Button */}
      <div 
        className="relative flex-shrink-0 w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
        onClick={() => !uploading && fileInputRef.current?.click()}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          multiple
          accept="image/jpeg,image/png,image/webp,image/heic"
          className="hidden"
        />
        {uploading ? (
           <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent"></div>
        ) : (
           <PlusIcon className="w-8 h-8 text-gray-400" />
        )}
      </div>
    </div>
  );
};

ImageCarousel.propTypes = {
  noteId: PropTypes.string.isRequired,
  uid: PropTypes.string.isRequired,
  images: PropTypes.arrayOf(PropTypes.string),
  onImagesUpdate: PropTypes.func.isRequired,
  onImageClick: PropTypes.func.isRequired,
};

export default ImageCarousel;
